// InlineRenamePopover.js

import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';

export default function InlineRenamePopover({
    targetRect,         // DOMRect об’єкта, який перейменовуємо
    initial,
    onSubmit,
    onCancel,
    gap = 8,            // відступ під елементом
    maxWidth = 320,
}) {
    const [value, setValue] = useState(initial || '');
    const [pos, setPos] = useState({ left: 0, top: 0, width: Math.min(280, maxWidth) });
    const popRef = useRef(null);
    const inputRef = useRef(null);

    // autofocus + select
    useEffect(() => { inputRef.current?.select(); }, []);

    // клавіші / клік поза
    useEffect(() => {
        const onKey = (e) => {
            if (e.key === 'Escape') onCancel();
            if (e.key === 'Enter') {
                e.preventDefault();
                const v = value.trim();
                if (v) onSubmit(v);
            }
        };
        const onDown = (e) => {
            if (!popRef.current) return;
            if (!popRef.current.contains(e.target)) onCancel();
        };
        document.addEventListener('keydown', onKey, true);
        document.addEventListener('mousedown', onDown, true);
        return () => {
            document.removeEventListener('keydown', onKey, true);
            document.removeEventListener('mousedown', onDown, true);
        };
    }, [value, onCancel, onSubmit]);

    // авто-позиціювання: “під елементом, вирівняно по лівому краю”, інакше — “по правому”
    useLayoutEffect(() => {
        if (!targetRect) return;

        const vw = window.innerWidth;
        const ph = popRef.current?.offsetHeight || 0;

        const desiredWidth = Math.min(maxWidth, Math.max(220, targetRect.width)); // зручно, але обмежено
        const belowTop = Math.round(targetRect.bottom + gap);

        // пробуємо вирівняти ліворуч
        let left = Math.round(targetRect.left);
        // якщо не влазить праворуч — пришиваємо правий край
        if (left + desiredWidth > vw - 8) {
            left = Math.max(8, Math.round(targetRect.right - desiredWidth));
        }

        // clamp по екрану
        left = Math.min(Math.max(8, left), Math.max(8, vw - desiredWidth - 8));
        setPos({ left, top: belowTop, width: desiredWidth, height: ph });
    }, [targetRect, gap, maxWidth]);

    return (
        <div
            ref={popRef}
            className="fixed z-[100] rounded-lg border bg-white p-3 shadow-xl dark:bg-neutral-900 dark:border-neutral-800"
            style={{ left: pos.left, top: pos.top, width: pos.width }}
        >
            <div className="flex flex-col items-stretch">
                <input
                    ref={inputRef}
                    type="text"
                    value={value}
                    onChange={(e) => setValue(e.target.value)}
                    className="w-full rounded-md border px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-blue-500 dark:bg-neutral-800"
                    placeholder="Нове ім’я"
                />
                <div className="mt-3 flex items-center justify-center gap-2">
                    <button
                        onClick={() => { const v = value.trim(); if (v) onSubmit(v); }}
                        className="rounded-md bg-blue-600 px-4 py-1.5 text-xs font-semibold text-white hover:bg-blue-700"
                    >
                        OK
                    </button>
                    <button
                        onClick={onCancel}
                        className="rounded-md border px-4 py-1.5 text-xs hover:bg-gray-50 dark:border-neutral-700 dark:hover:bg-neutral-800"
                    >
                        Скасувати
                    </button>
                </div>
            </div>
        </div>
    );
}

InlineRenamePopover.propTypes = {
    targetRect: PropTypes.object.isRequired, // DOMRect
    initial: PropTypes.string,
    onSubmit: PropTypes.func.isRequired,
    onCancel: PropTypes.func.isRequired,
    gap: PropTypes.number,
    maxWidth: PropTypes.number,
};
